/**
 * @fileoverview Module utilitaire regroupant les fonctions transversales pures.
 * Ce fichier ne contient aucune logique métier liée au DOM ou à la base de données.
 * Il agit comme une "boîte à outils" (manipulation de chaînes, calculs, logs)
 * destinée à être importée par les autres modules.
 */

import { CONFIG } from './config.js';

// ==========================================
// --- DÉBOGAGE ET LOGS ---
// ==========================================

/**
 * Affiche un message d'information dans la console uniquement si le mode debug est activé.
 * @param {...any} args - Les données, objets ou messages à afficher.
 */
export function debugLog(...args) {
    if (CONFIG.DEBUG_MODE) {
        console.log('%c[DEBUG]', 'color: #f59f00; font-weight: bold;', ...args);
    }
}

/**
 * Affiche un message d'erreur dans la console uniquement si le mode debug est activé.
 * @param {...any} args - Les erreurs, objets ou messages à afficher.
 */
export function debugError(...args) {
    if (CONFIG.DEBUG_MODE) {
        console.error('[ERREUR]', ...args);
    }
}

// ==========================================
// --- MANIPULATION DE TEXTE ET DONNÉES ---
// ==========================================

/**
 * Normalise une chaîne de caractères pour faciliter la recherche textuelle.
 * Convertit en minuscules, supprime les espaces superflus et retire les accents (diacritiques).
 * @param {string} text - Le texte brut à nettoyer.
 * @returns {string} Le texte normalisé et sans accents.
 */
export function cleanText(text) {
    if (!text) return '';
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

/**
 * Formate une taille en octets vers une unité lisible par l'humain (Ko, Mo, Go),
 * en arrondissant à la dizaine la plus proche pour une lecture rapide.
 * @param {number} bytes - La taille à formater, en octets.
 * @returns {string} La chaîne formatée (ex: "120 Ko", "< 10 Mo").
 */
export function formatBytes(bytes) {
    if (bytes === 0) return '0 o';

    const k = 1024;
    const sizes = ['o', 'Ko', 'Mo', 'Go'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    const rounded = Math.round(value / 10) * 10;

    // Si la valeur est trop petite pour atteindre 10 après arrondi, on indique "< 10"
    return (rounded === 0 && value > 0 ? '< 10' : rounded) + ' ' + sizes[i];
}

/**
 * Normalise un ISBN en retirant tous les tirets et espaces.
 * @param {string} isbn
 * @returns {string}
 */
export function normalizeIsbn(isbn) {
    return isbn.replace(/[-\s]/g, '').trim();
}

/**
 * Formate un timestamp en chaîne "JJ/MM/AAAA" en heure locale.
 * @param {number|null|undefined} timestamp - Millisecondes depuis epoch.
 * @returns {string} La date formatée, ou chaîne vide si entrée invalide.
 */
export function formatDate(timestamp) {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
}
